// AnteilFeld.tsx — die Quote EINER Rolle.
//
// Bis 08/2026 gab es genau ein Anteilsfeld, fest an der Rolle „Gesellschafter".
// Kommanditistin (§ 171 HGB, Betrag in Euro), Wohnungs- und Miteigentümerin
// (Bruchteil wie 250/1000) und Aktionärin konnten ihre Quote gar nicht erfassen —
// obwohl die Matrix den `quotenTyp` je Rechtsform × Rolle bis ins Frontend
// liefert und ihn dort niemand auswertete.
//
// Gespeichert wird der Text mit seiner Einheit (siehe utils/anteilFormat): Die
// sechs Anzeigestellen und das Organigramm lesen `share` roh, ein „250/1000"
// darf dort nicht als Prozentwert erscheinen.

import * as React from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import {
  ANTEIL_LABEL,
  ANTEIL_MAX,
  ANTEIL_PLATZHALTER,
  anteilHinweis,
  formatiereAnteil,
  type QuotenTyp,
} from "../../utils/anteilFormat";
import { dropLeadingZero, reformatWithCaret } from "../../utils/numberGrouping";

const ACC = "#7d2169";
const INK = "#241522";
const MUTED = "#6b5a66";
const LINE = "#ddd3dc";

const useStyles = makeStyles({
  field: { display: "flex", flexDirection: "column", rowGap: "4px" },
  lbl: {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: MUTED,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  inset: { display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: "10px", rowGap: "6px" },
  input: {
    ...shorthands.border("1px", "solid", LINE),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("7px", "10px"),
    fontSize: "0.86rem",
    color: INK,
    backgroundColor: "#fff",
    outlineStyle: "none",
    width: "160px",
    ":focus": { ...shorthands.borderColor(ACC), boxShadow: "0 0 0 3px rgba(125,33,105,0.13)" },
  },
  hint: { fontSize: "0.72rem", color: MUTED, lineHeight: 1.4 },
});

export function AnteilFeld({
  id,
  rollenLabel,
  quotenTyp,
  value,
  onChange,
}: {
  id: string;
  /** Name der Rolle, zu der diese Quote gehört — bei mehreren Feldern der Unterschied. */
  rollenLabel: string;
  quotenTyp: QuotenTyp;
  value: string;
  onChange: (v: string) => void;
}) {
  const s = useStyles();
  // Eigener Textstand, damit das Tippen nicht bei jedem Zeichen kanonisiert wird
  // („25 %" mitten in der Eingabe wäre nach dem dritten Zeichen im Weg).
  const [text, setText] = React.useState(value ?? "");
  const feldRef = React.useRef<HTMLInputElement | null>(null);

  // Von außen gesetzte Werte übernehmen (Popup öffnet mit Bestand), aber ein
  // unberührtes Feld nie umschreiben.
  React.useEffect(() => {
    const extern = value ?? "";
    setText((bisher) => (bisher === extern ? bisher : extern));
  }, [value]);

  const setzen = (v: string) => {
    setText(v);
    onChange(v);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const roh = e.target.value;
    if (quotenTyp === "EURO") {
      // Live-Gruppierung wie in den Geldfeldern der Analyse. Freitext („ca. 25")
      // muss durchkommen — deshalb NICHT GroupedNumberInput, dessen onChange
      // beim Blur eine Zahl liefert und den Text verwirft.
      const el = feldRef.current;
      const caret = el?.selectionStart ?? roh.length;
      const next = reformatWithCaret(roh, caret);
      const ohneNull = dropLeadingZero(next.text, next.caret);
      setzen(ohneNull.text);
      requestAnimationFrame(() => el?.setSelectionRange(ohneNull.caret, ohneNull.caret));
      return;
    }
    if (quotenTyp === "BRUCHTEIL") {
      // Nur Ziffern und höchstens ein Schrägstrich — alles andere wäre kein Bruch.
      const gefiltert = roh.replace(/[^\d/]/g, "");
      const teile = gefiltert.split("/");
      setzen(teile.length > 2 ? `${teile[0]}/${teile.slice(1).join("")}` : gefiltert);
      return;
    }
    setzen(roh);
  };

  // Beim Verlassen die Einheit anhängen — nur wenn der Wert sauber parst.
  const handleBlur = () => {
    const kanonisch = formatiereAnteil(text, quotenTyp);
    if (kanonisch !== text) setzen(kanonisch);
  };

  const hinweis = anteilHinweis(text, quotenTyp);

  return (
    <div className={s.field}>
      <span className={s.lbl}>
        {rollenLabel} — {ANTEIL_LABEL[quotenTyp]}
      </span>
      <div className={s.inset}>
        {/* Bewusst ohne Ober-/Untergrenze und ohne Blockade: Gebräuchlich sind
            auch ungefähre Angaben wie „ca. 25" — eine harte Prüfung würde sie
            abweisen (PO 15.08.2026). Der Hinweis darunter reicht.
            Kein Einheiten-Zusatz neben dem Feld: Die Einheit steht im Wert
            selbst, sonst stünde sie nach dem ersten Verlassen doppelt da. */}
        <input
          ref={feldRef}
          id={id}
          className={s.input}
          inputMode={quotenTyp === "EURO" ? "decimal" : "text"}
          maxLength={ANTEIL_MAX}
          placeholder={ANTEIL_PLATZHALTER[quotenTyp]}
          value={text}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </div>
      {hinweis && <span className={s.hint}>{hinweis}</span>}
    </div>
  );
}
